import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { districtManagerService } from '../services/districtManagerService';
import { useAuth } from '../context/AuthContext';
import ImageViewerModal from '../components/ImageViewerModal';
import { formatInspectorError } from '../utils/errorUtils';
import { getMediaBaseUrl } from '../config';

const compressImage = (file, maxSizeMB = 0.7) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (event) => {
            const img = new Image();
            
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Configurar tamaño máximo: 1200px (consistente con inspectores)
                const maxWidth = 1200;
                const maxHeight = 1200;
                let width = img.width;
                let height = img.height;
                
                // Reducir si es más grande
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }
                
                canvas.width = width;
                canvas.height = height;
                
                // Dibujar imagen
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convertir a blob comprimido recursivamente hasta quedar bajo maxSizeMB (calidad mínima 0.4)
                let quality = 0.85;
                const compressRecursive = () => {
                    canvas.toBlob(
                        (blob) => {
                            const sizeMB = blob.size / (1024 * 1024);
                            if (sizeMB > maxSizeMB && quality > 0.4) {
                                quality -= 0.08;
                                compressRecursive();
                            } else {
                                const compressedFile = new File(
                                    [blob],
                                    file.name,
                                    { type: 'image/jpeg', lastModified: Date.now() }
                                );
                                console.log(`📦 [Manager] Compresión de foto: ${(file.size / 1024).toFixed(1)} KB -> ${(compressedFile.size / 1024).toFixed(1)} KB (calidad: ${quality.toFixed(2)})`);
                                resolve(compressedFile);
                            }
                        },
                        'image/jpeg',
                        quality
                    );
                };
                compressRecursive();
            };
            
            img.onerror = () => {
                reject(new Error(`${file.name} no es una imagen válida`));
            };
            
            img.src = event.target.result;
        };
        
        reader.onerror = () => {
            reject(new Error(`Error leyendo ${file.name}`));
        };
        
        reader.readAsDataURL(file);
    });
};

const Toast = ({ show, message, type, onClose }) => {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    const colors = {
        success: '#28a745',
        error: '#dc3545',
        warning: '#ffc107',
        info: '#17a2b8'
    };

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: colors[type] || '#007bff',
            color: type === 'warning' ? '#000' : 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '300px'
        }}>
            {message}
        </div>
    );
};

const UnrealizableDetailModal = ({ unrealizable, onClose, onApprove, onReject }) => {
    const [expandedImage, setExpandedImage] = useState(null);
    const [decisionData, setDecisionData] = useState({
        justification_duration: unrealizable.available_durations?.[0] || 30,
        manager_comments: '',
        evidence_photos: []
    });
    const [makingDecision, setMakingDecision] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationType, setConfirmationType] = useState(null); // 'approve' or 'reject'
    const [uploadProgress, setUploadProgress] = useState(0);

    /*
const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => resolve({
                    latitude: position.coords.latitude.toString(),
                    longitude: position.coords.longitude.toString()
                }),
                (error) => reject(error),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            reject(new Error('Geolocalizacion no soportada'));
        }
    });
};
*/

    const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE = 15 * 1024 * 1024; // 15MB
    const MAX_FILES = 5;
    const VALID_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
    
    const validFiles = [];
    const errors = [];
    
    // Paso 1: Validar antes de comprimir
    for (const file of files) {
        if (!VALID_TYPES.includes(file.type)) {
            errors.push(`❌ ${file.name}: tipo no soportado (usa JPG, PNG o WebP)`);
            continue;
        }
        
        if (file.size > MAX_SIZE) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
            errors.push(`❌ ${file.name}: ${sizeMB}MB (máx 15MB)`);
            continue;
        }
        
        if (validFiles.length + decisionData.evidence_photos.length >= MAX_FILES) {
            errors.push(`⚠️ Solo puedes subir máximo ${MAX_FILES} fotos`);
            break;
        }
        
        validFiles.push(file);
    }
    
    // Mostrar errores si los hay
    if (errors.length > 0) {
        alert('Problemas con las imágenes:\n\n' + errors.join('\n'));
    }
    
    // Paso 2: Comprimir archivos válidos
    if (validFiles.length > 0) {
        try {
            const compressedFiles = await Promise.all(
                validFiles.map(async (file) => {
                    try {
                        const compressed = await compressImage(file);
                        const originalSize = (file.size / (1024 * 1024)).toFixed(2);
                        const compressedSize = (compressed.size / (1024 * 1024)).toFixed(2);
                        console.log(`✅ ${file.name}: ${originalSize}MB → ${compressedSize}MB`);
                        return compressed;
                    } catch (error) {
                        errors.push(`Error comprimiendo ${file.name}: ${error.message}`);
                        return null;
                    }
                })
            );
            
            // Filtrar nulls y agregar
            const successful = compressedFiles.filter(f => f !== null);
            
            if (successful.length > 0) {
                setDecisionData(prev => ({
                    ...prev,
                    evidence_photos: [...prev.evidence_photos, ...successful].slice(0, MAX_FILES)
                }));
            }
            
            if (errors.length > 0) {
                alert('Algunos archivos tuvieron problemas:\n' + errors.join('\n'));
            }
        } catch (error) {
            alert('Error procesando imágenes: ' + error.message);
        }
    }
};

    const removeFile = (index) => {
        setDecisionData(prev => ({
            ...prev,
            evidence_photos: prev.evidence_photos.filter((_, i) => i !== index)
        }));
    };

    const requestConfirmation = (type) => {
        if (decisionData.evidence_photos.length === 0) {
            alert('Debe agregar al menos una foto de evidencia');
            return;
        }

        if (type === 'reject') {
            if (!decisionData.manager_comments.trim() || decisionData.manager_comments.length < 10) {
                alert('Los comentarios son obligatorios para rechazar (mínimo 10 caracteres)');
                return;
            }
        }

        setConfirmationType(type);
        setShowConfirmation(true);
    };

    const handleConfirmedAction = async () => {
    try {
        setMakingDecision(true);
        setShowConfirmation(false);
        setUploadProgress(10);

        // ❌ COMENTADO - Ya no obtiene ubicación
        // const location = await getCurrentLocation();
        
        // ✅ NUEVO - Se envía vacío (null)
        const location = { latitude: null, longitude: null };
        setUploadProgress(30);

        const actionData = { ...decisionData, ...location };
        setUploadProgress(50);

        if (confirmationType === 'approve') {
            await onApprove(unrealizable.id, actionData);
        } else {
            await onReject(unrealizable.id, actionData);
        }

        setUploadProgress(100);
    } catch (error) {
        alert(formatInspectorError(error));
        setUploadProgress(0);
    } finally {
        setMakingDecision(false);
    }
};

    const getDurationLabel = (duration) => {
        if (duration === -1) return 'INDEFINIDO';
        return `${duration} minutos`;
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '30px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '2px solid #f0f0f0',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{ margin: 0, color: '#333' }}>
                        Revision de Incidencia #{unrealizable.id}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#666'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '10px',
                    marginBottom: '20px'
                }}>
                    <h3 style={{ marginTop: 0, color: '#333' }}>Informacion del Tecnico</h3>
                    <p><strong>Tecnico:</strong> {unrealizable.technician?.full_name}</p>
                    <p><strong>Distrito:</strong> {unrealizable.technician?.distrito}</p>
                    <p><strong>Coordenadas:</strong> {unrealizable.latitude}, {unrealizable.longitude}</p>
                    <p><strong>Fecha:</strong> {new Date(unrealizable.submitted_at).toLocaleString()}</p>

                    <h3 style={{ marginTop: '20px', color: '#333' }}>Detalles de la Incidencia</h3>
                    <p><strong>Motivo:</strong> {unrealizable.reason}</p>
                    <p><strong>Comentarios:</strong> {unrealizable.comments}</p>

                    
                    {unrealizable.evidence_photo_path && (
                    <div style={{ marginTop: '15px' }}>
                        <p><strong>Evidencia del técnico:</strong></p>
                        {(() => {
                            const baseUrl = getMediaBaseUrl();
                            
                            const imageUrl = `${baseUrl}/static/uploads/${unrealizable.evidence_photo_path}`;
                            
                            return (
                                <img
                                    src={imageUrl}
                                    alt="Evidencia"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        border: '2px solid #ddd',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setExpandedImage(imageUrl)}
                                    onError={(e) => (e.target.style.display = 'none')}
                                />
                            );
                        })()}

                        {/* Modal para imagen expandida con soporte de botón Atrás del celular y tache X */}
                        <ImageViewerModal src={expandedImage} onClose={() => setExpandedImage(null)} />
                    </div>
                )}

                </div>

                <div>
                    <h3 style={{ color: '#333', marginBottom: '20px' }}>Tu Evaluacion</h3>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Duracion Justificada
                        </label>
                        <select
                            value={decisionData.justification_duration}
                            onChange={(e) => setDecisionData(prev => ({
                                ...prev,
                                justification_duration: parseInt(e.target.value)
                            }))}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        >
                            
                            {unrealizable.available_durations
                                    ?.filter((duration) => duration !== -1) // 🔥 Elimina la opción INDEFINIDO
                                    ?.filter((duration) => unrealizable.reason !== 'zona_complicada' || duration <= 120)
                                    .map((duration) => (
                                        <option key={duration} value={duration}>
                                            {`${duration} minutos`}
                                        </option>

                            ))}
                        </select>
                        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                            Las opciones varian segun la hora de envio del tecnico
                        </small>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Comentarios (Obligatorio para rechazo)
                        </label>
                        <textarea
                            value={decisionData.manager_comments}
                            onChange={(e) => setDecisionData(prev => ({
                                ...prev,
                                manager_comments: e.target.value
                            }))}
                            placeholder="Agrega tus comentarios sobre la evaluacion..."
                            rows="3"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '8px',
                                fontSize: '14px',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Fotos de Evidencia (Minimo 1, Maximo 5)
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            id="evidence-photos"
                        />
                        <label
                            htmlFor="evidence-photos"
                            style={{
                                display: 'block',
                                padding: '20px',
                                border: '2px dashed #007bff',
                                borderRadius: '8px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: '#f0f8ff'
                            }}
                        >
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📸</div>
                            <span style={{ color: '#007bff', fontWeight: '600' }}>
                                Agregar Fotos
                            </span>
                        </label>

                        {decisionData.evidence_photos.length > 0 && (
                            <div style={{ marginTop: '15px' }}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                                    Fotos seleccionadas ({decisionData.evidence_photos.length}/5)
                                </h4>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                    gap: '10px'
                                }}>
                                    {decisionData.evidence_photos.map((file, index) => (
                                        <div key={index} style={{
                                            position: 'relative',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            border: '2px solid #e1e5e9'
                                        }}>
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Preview ${index + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                            <button
                                                onClick={() => removeFile(index)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    background: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ×
                                            </button>
                                            <div style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                background: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '4px',
                                                fontSize: '10px',
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Barra de progreso */}
                {makingDecision && (
                    <div style={{
                        marginTop: '20px',
                        marginBottom: '10px'
                    }}>
                        <div style={{
                            background: '#e9ecef',
                            borderRadius: '10px',
                            height: '8px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                background: '#007bff',
                                height: '100%',
                                width: `${uploadProgress}%`,
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                        <div style={{
                            textAlign: 'center',
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '5px'
                        }}>
                            {uploadProgress < 30 ? 'Obteniendo ubicación...' :
                                uploadProgress < 50 ? 'Preparando datos...' :
                                    uploadProgress < 100 ? 'Enviando...' : 'Completado'}
                        </div>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'center',
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '2px solid #f0f0f0'
                }}>
                    <button
                        onClick={() => requestConfirmation('approve')}
                        disabled={makingDecision || decisionData.evidence_photos.length === 0}
                        style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '12px 25px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: (makingDecision || decisionData.evidence_photos.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (makingDecision || decisionData.evidence_photos.length === 0) ? 0.5 : 1
                        }}
                    >
                        Aprobar
                    </button>

                    <button
                        onClick={() => requestConfirmation('reject')}
                        disabled={makingDecision || !decisionData.manager_comments.trim() || decisionData.evidence_photos.length === 0}
                        style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '12px 25px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: (makingDecision || !decisionData.manager_comments.trim() || decisionData.evidence_photos.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (makingDecision || !decisionData.manager_comments.trim() || decisionData.evidence_photos.length === 0) ? 0.5 : 1
                        }}
                    >
                        Rechazar
                    </button>

                    <button
                        onClick={onClose}
                        disabled={makingDecision}
                        style={{
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '12px 25px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: makingDecision ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Cancelar
                    </button>
                </div>

                {/* Modal de confirmación */}
                {showConfirmation && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '400px',
                            width: '90%'
                        }}>
                            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>
                                {confirmationType === 'approve' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                            </h3>
                            <p style={{ color: '#666', margin: '0 0 20px 0' }}>
                                {confirmationType === 'approve'
                                    ? `¿Estás seguro de aprobar esta incidencia con ${getDurationLabel(decisionData.justification_duration)} de justificación?`
                                    : '¿Estás seguro de rechazar esta incidencia? Esta acción no se puede deshacer.'
                                }
                            </p>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={() => setShowConfirmation(false)}
                                    style={{
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmedAction}
                                    style={{
                                        background: confirmationType === 'approve' ? '#28a745' : '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DistrictManagerPage = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [pendingUnrealizables, setPendingUnrealizables] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedUnrealizable, setSelectedUnrealizable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [activeTab, setActiveTab] = useState('pending');
    const [selectedDistrict, setSelectedDistrict] = useState('todos');
    const [selectedPriority, setSelectedPriority] = useState('todas');
    const [selectedDateFilter, setSelectedDateFilter] = useState('todas');

    // Definir incidencias de alta prioridad
    const HIGH_PRIORITY_REASONS = ['zona_roja', 'zona_complicada', 'mal_clima', 'problemas_acceso'];

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    // Verificar si una incidencia es de alta prioridad
    const isHighPriority = (reason) => {
        return HIGH_PRIORITY_REASONS.includes(reason);
    };

    // Filtrar por fecha
    const filterByDate = (unrealizable) => {
        if (selectedDateFilter === 'todas') return true;

        const submittedDate = new Date(unrealizable.submitted_at);
        const now = new Date();
        const diffDays = Math.floor((now - submittedDate) / (1000 * 60 * 60 * 24));

        switch (selectedDateFilter) {
            case 'hoy':
                return diffDays === 0;
            case 'ultimos_3':
                return diffDays <= 3;
            case 'semana':
                return diffDays <= 7;
            default:
                return true;
        }
    };

    // Obtener lista única de distritos con contadores
    const getDistrictStats = () => {
        const stats = {};
        pendingUnrealizables.forEach(item => {
            const distrito = item.technician?.distrito || 'Sin distrito';
            stats[distrito] = (stats[distrito] || 0) + 1;
        });
        return stats;
    };

    // Filtrar incidencias por todos los criterios
    const filteredUnrealizables = pendingUnrealizables
        .filter(item => {
            // Filtro por distrito
            if (selectedDistrict !== 'todos') {
                const distrito = item.technician?.distrito || 'Sin distrito';
                if (distrito !== selectedDistrict) return false;
            }

            // Filtro por prioridad
            if (selectedPriority !== 'todas') {
                const isPriority = isHighPriority(item.reason);
                if (selectedPriority === 'alta' && !isPriority) return false;
                if (selectedPriority === 'normal' && isPriority) return false;
            }

            // Filtro por fecha
            return filterByDate(item);
        });

    const loadPendingUnrealizables = useCallback(async () => {
        try {
            setLoading(true);
            console.log('Cargando incidencias pendientes...');
            const response = await districtManagerService.getPendingUnrealizables();
            console.log('Respuesta completa:', response);

            if (response.success) {
                console.log('Guardando incidencias en estado...');
                setPendingUnrealizables(response.unrealizables || []);
            }
        } catch (error) {
            console.error('Error cargando incidencias:', error);
            showToast('Error cargando incidencias pendientes', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await districtManagerService.getMyApprovals();
            if (response.success) {
                setHistory(response.approvals || []);
            }
        } catch (error) {
            showToast('Error cargando historial', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'pending') {
            loadPendingUnrealizables();
        } else {
            loadHistory();
        }
    }, [activeTab, loadPendingUnrealizables, loadHistory]);

    const handleViewDetails = async (unrealizableId) => {
        try {
            const response = await districtManagerService.getUnrealizableDetail(unrealizableId);
            if (response.success) {
                setSelectedUnrealizable(response.unrealizable);
            }
        } catch (error) {
            showToast('Error cargando detalles', 'error');
        }
    };

    const handleApprove = async (unrealizableId, data) => {
        try {
            const response = await districtManagerService.approveUnrealizable(unrealizableId, data);
            if (response.success) {
                showToast('Incidencia aprobada correctamente', 'success');
                setSelectedUnrealizable(null);
                loadPendingUnrealizables();
            }
        } catch (error) {
            showToast(error.message || 'Error aprobando la incidencia', 'error');
        }
    };

    const handleReject = async (unrealizableId, data) => {
        try {
            const response = await districtManagerService.rejectUnrealizable(unrealizableId, data);
            if (response.success) {
                showToast('Incidencia rechazada correctamente', 'success');
                setSelectedUnrealizable(null);
                loadPendingUnrealizables();
            }
        } catch (error) {
            showToast(error.message || 'Error rechazando la incidencia', 'error');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: { background: '#28a745', color: 'white' },
            rejected: { background: '#dc3545', color: 'white' }
        };
        const labels = {
            approved: 'Aprobado',
            rejected: 'Rechazado'
        };
        return (
            <span style={{
                ...styles[status],
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
            }}>
                {labels[status]}
            </span>
        );
    };

    // DEBUG LOGS
    console.log('Renderizando...');
    console.log('activeTab:', activeTab);
    console.log('loading:', loading);
    console.log('pendingUnrealizables:', pendingUnrealizables);
    console.log('history:', history);

    const handleGoToMaintenanceMap = () => {
    const token = localStorage.getItem('control_campo_token');
    
    if (!token) {
        alert('No hay sesión activa. Por favor inicia sesión primero.');
        return;
    }
    
    const mapaURL = `https://analisisoperativo.com:8010/?token=${token}`;
    window.open(mapaURL, '_blank');
};

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
            <header style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <h1 style={{ margin: '0 0 5px 0', fontSize: '1.5rem' }}>Panel de Gerente de Distrito</h1>
                        <p style={{ margin: 0 }}>Bienvenido, {user?.full_name}</p>
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>Distrito: {user?.distrito}</p>
                    </div>
                    <button
                        onClick={logout}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            border: '2px solid white',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        Cerrar Sesion
                    </button>
                </div>
            </header>

            <main style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '20px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '30px',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '10px'
                    }}>
                        <button
                            onClick={() => setActiveTab('pending')}
                            style={{
                                padding: '15px 25px',
                                border: 'none',
                                background: activeTab === 'pending' ? '#007bff' : 'white',
                                color: activeTab === 'pending' ? 'white' : '#333',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Incidencias Pendientes ({pendingUnrealizables.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            style={{
                                padding: '15px 25px',
                                border: 'none',
                                background: activeTab === 'history' ? '#007bff' : 'white',
                                color: activeTab === 'history' ? 'white' : '#333',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Historial ({history.length})
                        </button>
                    </div>

                    <div>
                        <button
                            onClick={() => navigate('/admin')}
                            style={{
                                padding: '12px 22px',
                                border: '2px solid #10B981',
                                background: 'white',
                                color: '#10B981',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.3s ease',
                                fontSize: '14px',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#10B981';
                                e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#10B981';
                            }}
                        >
                            <span>📊</span> Reportes y Dashboard ({user?.distrito || 'Distrito'})
                        </button>
                    </div>
                </div>
                
                {activeTab === 'pending' && pendingUnrealizables.length > 0 && (
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        marginBottom: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>
                            Filtros
                        </h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px'
                        }}>
                            {/* Filtro por Distrito */}
                            <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '5px',
                                fontSize: '14px'
                            }}>
                                <span style={{ fontWeight: '600', color: '#555' }}>📍 Distrito</span>
                                <select
                                    value={selectedDistrict}
                                    onChange={(e) => setSelectedDistrict(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #e1e5e9',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="todos">Todos</option>
                                    {Object.entries(getDistrictStats()).map(([distrito, count]) => (
                                        <option key={distrito} value={distrito}>
                                            {distrito} ({count})
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {/* Filtro por Prioridad */}
                            <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '5px',
                                fontSize: '14px'
                            }}>
                                <span style={{ fontWeight: '600', color: '#555' }}>⚠️ Prioridad</span>
                                <select
                                    value={selectedPriority}
                                    onChange={(e) => setSelectedPriority(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #e1e5e9',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="todas">Todas</option>
                                    <option value="alta">Alta prioridad</option>
                                    <option value="normal">Normal</option>
                                </select>
                            </label>

                            {/* Filtro por Fecha */}
                            <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '5px',
                                fontSize: '14px'
                            }}>
                                <span style={{ fontWeight: '600', color: '#555' }}>📅 Fecha</span>
                                <select
                                    value={selectedDateFilter}
                                    onChange={(e) => setSelectedDateFilter(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #e1e5e9',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="todas">Todas</option>
                                    <option value="hoy">Hoy</option>
                                    <option value="ultimos_3">Últimos 3 días</option>
                                    <option value="semana">Esta semana</option>
                                </select>
                            </label>
                        </div>

                        {/* Resumen de filtros activos */}
                        <div style={{
                            marginTop: '15px',
                            padding: '10px',
                            background: '#f8f9fa',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            Mostrando <strong>{filteredUnrealizables.length}</strong> de <strong>{pendingUnrealizables.length}</strong> incidencias
                        </div>
                    </div>
                )}

                <div style={{
                    background: 'white',
                    borderRadius: '15px',
                    padding: '30px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            Cargando...
                        </div>
                    ) : activeTab === 'pending' ? (
                        <>
                            <h2>Incidencias Pendientes de Revision</h2>
                            {filteredUnrealizables.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                    {pendingUnrealizables.length === 0
                                        ? 'No hay incidencias pendientes'
                                        : `No hay incidencias en ${selectedDistrict}`
                                    }
                                </div>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                    gap: '20px',
                                    marginTop: '20px'
                                }}>
                                    {filteredUnrealizables.map((unrealizable) => (
                                        <div key={unrealizable.id} style={{
                                            border: isHighPriority(unrealizable.reason) ? '2px solid #ff6b6b' : '2px solid #e9ecef',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            transition: 'all 0.3s ease',
                                            position: 'relative',
                                            background: isHighPriority(unrealizable.reason) ? '#fff5f5' : 'white'
                                        }}>
                                            {isHighPriority(unrealizable.reason) && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    right: '10px',
                                                    background: '#ff6b6b',
                                                    color: 'white',
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    ⚠️ PRIORIDAD ALTA
                                                </div>
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '15px',
                                                marginTop: isHighPriority(unrealizable.reason) ? '25px' : '0'
                                            }}>
                                                <h3 style={{ margin: 0 }}>Incidencia #{unrealizable.id}</h3>
                                                <span style={{
                                                    background: '#ffc107',
                                                    color: '#212529',
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {unrealizable.reason}
                                                </span>
                                            </div>

                                            <div style={{ color: '#666' }}>
                                                <p style={{ margin: '8px 0' }}>
                                                    <strong>Tecnico:</strong> {unrealizable.technician?.full_name}
                                                </p>
                                                <p style={{ margin: '8px 0' }}>
                                                    <strong>Distrito:</strong> {unrealizable.technician?.distrito}
                                                </p>
                                                <p style={{ margin: '8px 0' }}>
                                                    <strong>Comentarios:</strong> {unrealizable.comments}
                                                </p>
                                                <p style={{ margin: '8px 0' }}>
                                                    <strong>Fecha:</strong> {new Date(unrealizable.submitted_at).toLocaleString()}
                                                </p>
                                            </div>

                                            <button
                                                onClick={() => handleViewDetails(unrealizable.id)}
                                                style={{
                                                    marginTop: '15px',
                                                    width: '100%',
                                                    padding: '10px 20px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    background: '#007bff',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                Ver Detalles
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <h2>Historial de Decisiones</h2>
                            {history.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                    No hay decisiones registradas
                                </div>
                            ) : (
                                <div style={{ marginTop: '20px' }}>
                                    {history.map((approval) => (
                                        <div key={approval.id} style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            marginBottom: '15px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '15px'
                                            }}>
                                                <h3 style={{ margin: 0 }}>Decision #{approval.id}</h3>
                                                {getStatusBadge(approval.status)}
                                            </div>

                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: '15px',
                                                color: '#666'
                                            }}>
                                                <div>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Tecnico:</strong> {approval.unrealizable?.technician}
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Distrito:</strong> {approval.unrealizable?.distrito}
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Motivo:</strong> {approval.unrealizable?.reason}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Duracion:</strong> {approval.justification_duration === -1 ? 'INDEFINIDO' : `${approval.justification_duration} min`}
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Fotos:</strong> {approval.evidence_photos_count}
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        <strong>Fecha:</strong> {new Date(approval.decided_at).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>

                                            {approval.manager_comments && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    background: '#f8f9fa',
                                                    borderRadius: '6px'
                                                }}>
                                                    <strong>Tus comentarios:</strong>
                                                    <p style={{ margin: '5px 0 0 0', color: '#666' }}>
                                                        {approval.manager_comments}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>

            {selectedUnrealizable && (
                <UnrealizableDetailModal
                    unrealizable={selectedUnrealizable}
                    onClose={() => setSelectedUnrealizable(null)}
                    onApprove={handleApprove}
                    onReject={handleReject}
                />
            )}

            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: '' })}
            />
        </div>
    );
};

export default DistrictManagerPage;